import { makeAutoObservable, runInAction } from 'mobx';
import RootStore from '../../../stores/RootStore';

interface DeletionStats {
  panelCount: number;
  chipCount: number;
  productionScheduleCount: number;
  orphanedMoldInstanceCount: number;
}

interface Panel {
  id: number;
  projectId?: number;
  project_id?: number;
  moldInstanceId?: number;
  panelMark?: string;
  uniquePieceIdentifier?: string;
  panelType?: string;
  panelFamily?: string;
  location?: string;
  lengthInches?: number;
  widthInches?: number;
  depthInches?: number;
  volumeCuft?: number;
  weightLbs?: number;
  quantity?: number;
  status?: string;
}

interface Project {
  id: number;
  // projectsStore fields
  projectNumber?: string;
  projectName?: string;
  // jobStore fields
  jobNumber?: string | number;
  name?: string;
  status?: string;
  archived?: boolean;
}

class PanelManagementStore {
  rootStore: RootStore;

  selectedProjectId: number | null = null;
  selectedPanelIds: number[] = [];
  deletionStats: DeletionStats | null = null;
  isCalculating = false;
  isDeleting = false;
  panelsLoaded = false;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  setSelectedProject = async (projectId: number | null) => {
    runInAction(() => {
      this.selectedProjectId = projectId;
      this.deletionStats = null;
      this.selectedPanelIds = [];
      this.panelsLoaded = false;
    });

    if (projectId) {
      await this.loadPanelsForProject(projectId);
    }
  };

  loadPanelsForProject = async (projectId: number) => {
    const panelsStore = this.rootStore.panelsStore as {
      find?: (query: Record<string, unknown>) => Promise<void>;
    };

    if (panelsStore?.find) {
      await panelsStore.find({ query: { projectId } });
      runInAction(() => {
        this.panelsLoaded = true;
      });
    }
  };

  loadAllPanels = async () => {
    const panelsStore = this.rootStore.panelsStore as {
      find?: (query: Record<string, unknown>) => Promise<void>;
    };

    if (panelsStore?.find) {
      await panelsStore.find({ query: { $limit: 1000 } });
      runInAction(() => {
        this.panelsLoaded = true;
      });
    }
  };

  setSelectedPanels = (panelIds: number[]) => {
    runInAction(() => {
      this.selectedPanelIds = panelIds;
    });
  };

  togglePanelSelection = (panelId: number) => {
    runInAction(() => {
      const index = this.selectedPanelIds.indexOf(panelId);
      if (index > -1) {
        this.selectedPanelIds.splice(index, 1);
      } else {
        this.selectedPanelIds.push(panelId);
      }
    });
  };

  get activeProjects(): Project[] {
    // Try jobStore first (has name, jobNumber fields)
    const jobStore = this.rootStore.jobStore as {
      entities?: Project[];
    };
    if (jobStore?.entities?.length) {
      return jobStore.entities.filter((p) => !p.archived && p.status !== 'cancelled');
    }

    // Fall back to projectsStore
    const projectsStore = this.rootStore.projectsStore as {
      entities?: Project[];
    };
    return (projectsStore?.entities || []).filter(
      (p) => p.status === 'active'
    );
  }

  get allProjects(): Project[] {
    // Try jobStore first (has name, jobNumber fields)
    const jobStore = this.rootStore.jobStore as {
      entities?: Project[];
    };
    if (jobStore?.entities?.length) {
      return jobStore.entities;
    }

    // Fall back to projectsStore
    const projectsStore = this.rootStore.projectsStore as {
      entities?: Project[];
    };
    return projectsStore?.entities || [];
  }

  // Get job name by project ID - checks both jobStore and projectsStore
  getJobNameById = (projectId: number | undefined): string => {
    if (!projectId) return "Unknown Job";

    // First try jobStore (uses name, jobNumber fields)
    const jobStore = this.rootStore.jobStore as {
      entities?: Project[];
    };
    const job = (jobStore?.entities || []).find(j => j.id === projectId);
    if (job) {
      return job.name || `Job ${job.jobNumber}` || `Job #${projectId}`;
    }

    // Fall back to projectsStore (uses projectName, projectNumber fields)
    const projectsStore = this.rootStore.projectsStore as {
      entities?: Project[];
    };
    const project = (projectsStore?.entities || []).find(p => p.id === projectId);
    if (project) {
      return project.projectName || project.projectNumber || `Job #${projectId}`;
    }

    return `Unknown Job (ID: ${projectId})`;
  };

  get panelsForSelectedProject(): Panel[] {
    if (!this.selectedProjectId) return [];
    const panelsStore = this.rootStore.panelsStore as {
      entities?: Panel[];
    };
    return (panelsStore?.entities || []).filter(
      (p) => (p.projectId || p.project_id) === this.selectedProjectId
    );
  }

  // Helper to get project ID from panel (handles both camelCase and snake_case)
  getPanelProjectId = (panel: Panel): number | undefined => {
    return panel.projectId || panel.project_id;
  };

  get allPanels(): Panel[] {
    const panelsStore = this.rootStore.panelsStore as {
      entities?: Panel[];
    };
    return panelsStore?.entities || [];
  }

  /**
   * Returns a map of project ID to panel count
   */
  get panelCountsByProject(): Map<number, number> {
    const counts = new Map<number, number>();
    for (const panel of this.allPanels) {
      const projectId = panel.projectId || panel.project_id;
      if (projectId) {
        counts.set(projectId, (counts.get(projectId) || 0) + 1);
      }
    }
    return counts;
  }

  /**
   * Get panel count for a specific project
   */
  getPanelCountForProject = (projectId: number): number => {
    return this.panelCountsByProject.get(projectId) || 0;
  };

  calculateDeletionStats = async (panelIds: number[]): Promise<DeletionStats> => {
    runInAction(() => {
      this.isCalculating = true;
    });

    try {
      const stats: DeletionStats = {
        panelCount: panelIds.length,
        chipCount: 0,
        productionScheduleCount: 0,
        orphanedMoldInstanceCount: 0,
      };

      // Define store types with find method
      type StoreWithFind = {
        entities?: { panelId?: number; panel_id?: number }[];
        find?: (query: Record<string, unknown>) => Promise<unknown>;
      };

      const chipsStore = this.rootStore.panelScheduleChipsStore as StoreWithFind;
      const prodScheduleStore = this.rootStore.productionScheduleStore as StoreWithFind;

      // Only fetch from stores that have existing tables
      // (reinforcementSchedule, productionTickets, embedsAndInserts tables don't exist yet)
      try {
        if (chipsStore?.find) {
          await chipsStore.find({ query: { panelId: { $in: panelIds }, $limit: 10000 } });
        }
      } catch {
        // Table may not exist, ignore
      }

      try {
        if (prodScheduleStore?.find) {
          await prodScheduleStore.find({ query: { panelId: { $in: panelIds }, $limit: 10000 } });
        }
      } catch {
        // Table may not exist, ignore
      }

      // Count from the loaded entities
      for (const panelId of panelIds) {
        stats.chipCount += (chipsStore?.entities || []).filter(
          (c) => (c.panelId || c.panel_id) === panelId
        ).length;
        stats.productionScheduleCount += (prodScheduleStore?.entities || []).filter(
          (p) => (p.panelId || p.panel_id) === panelId
        ).length;
      }

      // Calculate orphaned mold instances
      const panelIdsSet = new Set(panelIds);
      const panelsToDelete = this.allPanels.filter((p) => panelIdsSet.has(p.id));

      // Get mold instance IDs from panels being deleted
      const moldInstanceIds = new Set(
        panelsToDelete
          .map((p) => p.moldInstanceId)
          .filter((id): id is number => id != null)
      );

      // For each mold instance, check if other panels still use it
      for (const instanceId of moldInstanceIds) {
        const remainingPanels = this.allPanels.filter(
          (p) => p.moldInstanceId === instanceId && !panelIdsSet.has(p.id)
        );
        if (remainingPanels.length === 0) {
          stats.orphanedMoldInstanceCount++;
        }
      }

      runInAction(() => {
        this.deletionStats = stats;
        this.isCalculating = false;
      });

      return stats;
    } catch (error) {
      runInAction(() => {
        this.isCalculating = false;
      });
      throw error;
    }
  };

  deletePanelsWithCleanup = async (panelIds: number[]): Promise<void> => {
    runInAction(() => {
      this.isDeleting = true;
    });

    try {
      const panelsStore = this.rootStore.panelsStore as {
        entities?: Panel[];
        remove?: (id: number) => Promise<void>;
      };
      const prodScheduleStore = this.rootStore.productionScheduleStore as {
        entities?: { id: number; panelId: number }[];
        remove?: (id: number) => Promise<void>;
      };
      const moldInstancesStore = this.rootStore.moldInstancesStore as {
        remove?: (id: number) => Promise<void>;
      };

      // Identify orphaned mold instances BEFORE deleting panels
      const panelIdsSet = new Set(panelIds);
      const panelsToDelete = (panelsStore?.entities || []).filter((p) =>
        panelIdsSet.has(p.id)
      );

      // Get mold instance IDs from panels being deleted
      const moldInstanceIds = new Set(
        panelsToDelete
          .map((p) => p.moldInstanceId)
          .filter((id): id is number => id != null)
      );

      // Find which mold instances will become orphaned
      const orphanedMoldInstanceIds: number[] = [];
      for (const instanceId of moldInstanceIds) {
        const remainingPanels = (panelsStore?.entities || []).filter(
          (p) => p.moldInstanceId === instanceId && !panelIdsSet.has(p.id)
        );
        if (remainingPanels.length === 0) {
          orphanedMoldInstanceIds.push(instanceId);
        }
      }

      for (const panelId of panelIds) {
        // 1. Manual cleanup of production_schedule (BEFORE panel deletion)
        const prodItems = (prodScheduleStore?.entities || []).filter(
          (p) => p.panelId === panelId
        );
        for (const item of prodItems) {
          if (prodScheduleStore?.remove) {
            await prodScheduleStore.remove(item.id);
          }
        }

        // 2. Delete the panel itself (panel_schedule_chips CASCADE automatically)
        if (panelsStore?.remove) {
          await panelsStore.remove(panelId);
        }
      }

      // 3. Delete orphaned mold instances (AFTER panel deletion)
      for (const instanceId of orphanedMoldInstanceIds) {
        if (moldInstancesStore?.remove) {
          try {
            await moldInstancesStore.remove(instanceId);
          } catch {
            // Mold instance may have been deleted by another process, ignore
          }
        }
      }

      runInAction(() => {
        this.isDeleting = false;
        this.selectedPanelIds = [];
        this.deletionStats = null;
      });
    } catch (error) {
      runInAction(() => {
        this.isDeleting = false;
      });
      throw error;
    }
  };

  reset = () => {
    runInAction(() => {
      this.selectedProjectId = null;
      this.selectedPanelIds = [];
      this.deletionStats = null;
      this.isCalculating = false;
      this.isDeleting = false;
      this.panelsLoaded = false;
    });
  };
}

export default PanelManagementStore;
